/* eslint-disable max-len */
import {
    all,
    call,
    put,
    takeLatest,
} from 'redux-saga/effects';
import {
    API_SEND_TAN,
    API_LOGIN,
    ACTION_LOGIN,
    CONTAINER_BTX_TEMPLATE,
    ACTION_SEND_TAN,
    ACTION_ELIGIBILITY_CHECK,
    API_SUBSCRIPTION_DETAILS,
    API_HEADER_ACCEPT,
    TRACKING_ERROR_TYPE_VALIDATION,
    TRACKING_ERROR_AREA_MSISDN,
    TRACKING_ERROR_AREA_MTAN,
    API_ELIGIBILITY_GIGAKOMBI,
    ACTION_INIT_PAGE,
    API_CUSTOMER,
    CUSTOMER_TYPE_SOHO,
    CUSTOMER_TYPE_FAF,
    CUSTOMER_TYPE_YOUNG,
    CUSTOMER_TYPE_CONSUMER,
    BTX_DECIDE,
    BTX_MULTICARD_CONTRACT,
    BTX_REDPLUS,
    BTX_MULTISIM,
    API_ELIGIBILITY_REDPLUS,
    API_ELIGIBILITY_TARIFFCHANGE,
    API_ELIGIBILITY_VVL,
    API_ELIGIBILITY_MULTICARD_CONTRACT,
    BTX_VVL,
    BTX_TARIFF_CHANGE,
    ErrorCodes,
    BTX_MNP,
    MNP_FLOW,
} from './constant';
import {
    ExtractDataType,
    requestHtml,
    requestJson,
    RequestResponse,
} from '@vfde-sails/utils';
import {
    AppActionCreators,
    actionCreators,
} from './action';
import {
    ECAPIResponse,
    IAPIResponseECRedplus,
    IAPIResponseECUltraCard,
    IAPIResponseError,
    IAuthenticationBody,
} from './interfaces/api.interface';
import {
    TrackingErrorType,
    trackIt,
} from '@vfde-sails/tracking';
import {
    BtxType,
    EligibilityResult,
    EndpointType,
} from './interfaces/main.interface';
import { SagaIterator } from '@redux-saga/types';
import { redirectToEndpoint } from './helper/endpointHandler.helper';
import { mountUIElements } from './helper/mountUI.helper';
import {
    isEligibleForInlife,
    isEligibleForMulticardContract,
    isEligibleForRedplus,
    isEligibleForVVL,
    isVVLorTariffChange,
} from './helper/handleEligibility.helper';
import { getCurrentBtx } from './helper/getUrlParams.helper';
import { normalizePhoneNumber } from './helper/validator.helper';

const { addComponents, setMtanStep, initPageSuccess, setError, sendTanSuccess, setIsLoggedIn } = actionCreators;

/**
 *  Init the page and setup the UI on load
 */
export function* initPageSaga ({ btx, formActions }: ReturnType<AppActionCreators['initPage']>): SagaIterator {
    const results = yield all([
        call(mountUIElements, formActions, btx),
        call(getTemplateSaga, { btx }),
    ]);

    yield all([
        put(addComponents(results[0])),
        put(setMtanStep(0)),
        put(initPageSuccess()),
    ]);
}

/**
 * fetch html template snippets according to current btx
 */
export function* getTemplateSaga ({ btx }: { btx: BtxType }): SagaIterator {
    try {
        const path = window.additionalPageOptions.templates[btx];

        const options = {
            method: 'get',
            headers: {
                [API_HEADER_ACCEPT]: '*/*',
                'Content-Type': 'text/html; charset=ISO-8859-1',
            },
        };
        const container = document.getElementById(CONTAINER_BTX_TEMPLATE);

        if (container) {
            const response: string = yield call(requestHtml, path, options);
            container.innerHTML = response;
        }
    }
    catch (error: any) {
        yield put(setError({
            type: TRACKING_ERROR_TYPE_VALIDATION,
            area: TRACKING_ERROR_AREA_MSISDN,
            code: ErrorCodes.TechnicalError,
        }));
    }
}

/**
 * Request a new MTAN with the given phone number (after phone was validated)
 */
export function* sendTanSaga ({ phoneNumber }: ReturnType<AppActionCreators['sendTan']>): SagaIterator {
    try {
        const bodyObj: IAuthenticationBody = { username: normalizePhoneNumber(phoneNumber) };

        if (getCurrentBtx() === BTX_MNP) {
            bodyObj.flow = MNP_FLOW;
        }

        const options = {
            method: 'post',
            headers: {
                [API_HEADER_ACCEPT]: 'application/json',
            },
            body: JSON.stringify(bodyObj),
        };

        yield call(requestJson, API_SEND_TAN, options);
        yield put(setMtanStep(1));
        yield put(sendTanSuccess());
    }
    catch (error: any) {
        const errorResponse: IAPIResponseError = error;
        const { status } = errorResponse.body;

        if (errorResponse) {
            yield put(setError({
                type: TRACKING_ERROR_TYPE_VALIDATION,
                area: TRACKING_ERROR_AREA_MSISDN,
                code: status!.toString(),
            }));
        }
    }
}

/**
 * Read the sms code and the phone number from the inputs and send them to authenticate
 */
export function* loginSaga ({ phoneNumber, tan }: ReturnType<AppActionCreators['login']>): SagaIterator {
    try {
        const bodyObj: IAuthenticationBody = { username: normalizePhoneNumber(phoneNumber), tan };

        if (getCurrentBtx() === BTX_MNP) {
            bodyObj.flow = MNP_FLOW;
        }

        const options = {
            method: 'post',
            headers: {
                [API_HEADER_ACCEPT]: 'application/json',
            },
            body: JSON.stringify(bodyObj),
        };

        yield call(requestJson, API_LOGIN, options);
        yield put(setIsLoggedIn(true));
        getCurrentBtx() === BTX_MULTISIM && sessionStorage.setItem('bShop_login', '1');
    }
    catch (error: any) {
        const errorResponse: IAPIResponseError = error;

        if (errorResponse) {
            yield put(setError({
                type: TrackingErrorType.Authentication,
                area: TRACKING_ERROR_AREA_MTAN,
                code: ErrorCodes.LoginFailed,
            }));
        }
    }
}

/**
 * Fetch data to identify the customer type
 */
export function* getCustomerTypeSaga (): SagaIterator {
    try {
        const options = {
            method: 'get',
            headers: {
                [API_HEADER_ACCEPT]: 'application/json',
            },
        };

        const response = yield call(requestJson, API_CUSTOMER, options);
        const { customerflags: { issohocustomer, isfriendsandfamilycustomer, isyoungcustomer } } = response.body.data;

        switch (true) {
            case issohocustomer:
                return CUSTOMER_TYPE_SOHO;
            case isfriendsandfamilycustomer:
                return CUSTOMER_TYPE_FAF;
            case isyoungcustomer:
                return CUSTOMER_TYPE_YOUNG;
            default: {
                return CUSTOMER_TYPE_CONSUMER;
            }
        }
    }
    catch (error: any) {
        const errorResponse: IAPIResponseError = error;

        if (errorResponse) {
            yield put(setError({
                type: TRACKING_ERROR_TYPE_VALIDATION,
                area: TRACKING_ERROR_AREA_MTAN,
                code: ErrorCodes.TechnicalError,
            }));
        }
    }
}

/**
 * eligibility check in order to see if customer is eligible for the chosen flow
 */
export function* eligibilityCheckSaga ({ btx, mtanStep = 0 }: Partial<ReturnType<AppActionCreators['eligibilityCheck']>>): SagaIterator {
    try {
        const options = {
            method: 'get',
            headers: {
                [API_HEADER_ACCEPT]: 'application/json',
            },
        };

        let eligibilityResult: EligibilityResult;

        switch (btx) {
            case BTX_MULTISIM:
            case BTX_MULTICARD_CONTRACT: {
                const customerData = ((yield call(requestJson, API_ELIGIBILITY_MULTICARD_CONTRACT, options)) as RequestResponse<ECAPIResponse>).body?.data;
                eligibilityResult = isEligibleForMulticardContract(customerData as ExtractDataType<IAPIResponseECUltraCard>);
                break;
            }
            case BTX_REDPLUS: {
                const customerData = ((yield call(requestJson, API_ELIGIBILITY_REDPLUS, options)) as RequestResponse<ECAPIResponse>).body?.data;
                eligibilityResult = isEligibleForRedplus(customerData as ExtractDataType<IAPIResponseECRedplus>);
                break;
            }
            case BTX_DECIDE: {
                const customerType = yield call(getCustomerTypeSaga);

                const [redplusEligibility, inlifeEligibility, subDetailsResponse, vvlEligibility, gigakombiEligibility] = yield all([
                    call(requestJson, API_ELIGIBILITY_REDPLUS, options),
                    call(requestJson, `${API_ELIGIBILITY_TARIFFCHANGE}/online.${customerType}`, options),
                    call(requestJson, API_SUBSCRIPTION_DETAILS, options),
                    call(requestJson, API_ELIGIBILITY_VVL, options),
                    call(requestJson, API_ELIGIBILITY_GIGAKOMBI, options),
                ]);

                const customerData = {
                    inlifeData: inlifeEligibility.body?.data,
                    contractProlongationData: vvlEligibility.body?.data,
                    gigaKombiData: gigakombiEligibility.body?.data,
                    subscriptionDetailsData: subDetailsResponse.body?.data,
                    redplusData: redplusEligibility.body?.data,
                    customerType: customerType,
                };

                eligibilityResult = isVVLorTariffChange(customerData);
                break;
            }
            case BTX_VVL: {
                const customerType = yield call(getCustomerTypeSaga);

                const [redplusEligibility, vvlEligibility, gigakombiEligibility] = yield all([
                    call(requestJson, API_ELIGIBILITY_REDPLUS, options),
                    call(requestJson, API_ELIGIBILITY_VVL, options),
                    call(requestJson, API_ELIGIBILITY_GIGAKOMBI, options),
                ]);

                const customerData = {
                    contractProlongationData: vvlEligibility.body?.data,
                    gigaKombiData: gigakombiEligibility.body?.data,
                    redplusData: redplusEligibility.body?.data,
                    customerType: customerType,
                };

                eligibilityResult = isEligibleForVVL(customerData);
                break;
            }
            case BTX_TARIFF_CHANGE: {
                const customerType = yield call(getCustomerTypeSaga);

                const [redplusEligibility, inlifeEligibility, subDetailsResponse] = yield all([
                    call(requestJson, API_ELIGIBILITY_REDPLUS, options),
                    call(requestJson, `${API_ELIGIBILITY_TARIFFCHANGE}/online.${customerType}`, options),
                    call(requestJson, API_SUBSCRIPTION_DETAILS, options),
                ]);

                const customerData = {
                    inlifeData: inlifeEligibility.body?.data,
                    redplusData: redplusEligibility.body?.data,
                    subscriptionDetailsData: subDetailsResponse.body?.data,
                    customerType: customerType,
                };

                eligibilityResult = isEligibleForInlife(customerData);
                break;
            }
            default: {
                return;
            }
        }

        if (eligibilityResult.eligible) {
            const currentBtx = eligibilityResult.endpointData?.btx || btx;
            const loginDescription = mtanStep === 1 ? 'Login' : 'Seamless Login';
            trackIt(`Store ${currentBtx} ${loginDescription}`, {});
            redirectToEndpoint(eligibilityResult.endpointData || { btx: currentBtx } as EndpointType);

            return;
        }

        if (eligibilityResult!.errorCode) {
            throw {
                body: {
                    errors: [],
                    status: eligibilityResult!.errorCode,
                },
            };
        }
    }
    catch (error: any) {
        const errorResponse: IAPIResponseError = error;

        if (errorResponse) {
            const { status } = errorResponse.body;
            yield put(setError({
                type: TrackingErrorType.Authentication,
                area: TRACKING_ERROR_AREA_MTAN,
                code: status!.toString(),
            }));
        }
    }
}

/**
 * Export generator function
 */
export function* authenticationSaga () {
    yield takeLatest(ACTION_INIT_PAGE, initPageSaga);
    yield takeLatest(ACTION_ELIGIBILITY_CHECK, eligibilityCheckSaga);
    yield takeLatest(ACTION_SEND_TAN, sendTanSaga);
    yield takeLatest(ACTION_LOGIN, loginSaga);
}
